<template>
  <div class="column full-height">
    <!-- Header - Only for normal (drawer) mode -->
    <template v-if="!isFloating">
      <header
        class="row items-center justify-between q-px-2x-md full-width no-wrap"
        style="height: 76px"
      >
        <span
          class="text-lg text-neutral text-weight-medium ellipsis"
          style="max-width: 75%"
        >
          {{ humanizeIdentifier(template?.elementName) || "-" }}
        </span>

        <PBtn color="primary" flat round @click="onClose">
          <CloseIcon class="icon" />
        </PBtn>
      </header>

      <q-separator color="neutral-40" />
    </template>

    <div class="col overflow-auto" :class="contentContainerClass">
      <!-- Collapsible Preview Section -->
      <div>
        <div
          class="flex items-center justify-between cursor-pointer q-mb-sm"
          @click="isFloating ? (previewExpanded = !previewExpanded) : null"
        >
          <div class="text-neutral-70" :class="headerTextClass">
            Template Preview
          </div>
          <PBtn
            v-if="isFloating"
            flat
            dense
            size="sm"
            color="primary"
            @click.stop="previewExpanded = !previewExpanded"
          >
            <span class="text-xxs">{{
              previewExpanded ? "Hide details" : "Show details"
            }}</span>
            <ChevronDownIcon
              class="icon--xxs q-ml-xxs"
              :class="previewExpanded ? 'rotate-180' : ''"
              :style="{ transition: 'transform 0.3s' }"
            />
          </PBtn>
        </div>

        <!-- Preview Content -->
        <TemplatePreview
          v-if="!isFloating || previewExpanded"
          :template
          :form-data
        />
      </div>

      <q-separator spaced="xl" color="neutral-40" />

      <q-form class="q-mt-md" @submit.prevent="onSubmit">
        <div class="grid items-center" :style="gridStyle">
          <template
            v-for="param in template?.customParams || []"
            :key="param.paramName"
          >
            <div class="col-4 q-mb-lg" :class="labelClass">
              {{ `\{\{ ${formatParamLabel(param.paramName)} \}\}` }}
            </div>
            <PInput
              v-model="formData[param.paramName]"
              :dense="isFloating"
              :label-classes="labelClass"
              :placeholder="'Enter ' + formatParamLabel(param.paramName)"
              class="prepend-bg col-8"
              required
              outlined
            >
              <template v-if="getPrepend(param.paramValue)" #prepend>
                <span class="text-sm">{{ getPrepend(param.paramValue) }}</span>
              </template>
            </PInput>
          </template>
        </div>

        <q-separator spaced="xl" color="neutral-40" />

        <!-- Buttons -->
        <div v-if="isFloating">
          <PBtn
            label="Send"
            color="primary"
            type="submit"
            class="full-width"
            :loading="sendingMessage"
            :disable="template?.customParams?.length ? !isFormValid : false"
          />
        </div>
        <div v-else class="flex justify-end">
          <PBtn
            color="primary"
            label="Cancel"
            outline
            :fluid="false"
            @click="onClose"
          />
          <PBtn
            class="q-ml-md"
            color="primary"
            label="Send"
            type="submit"
            :fluid="false"
            :loading="sendingMessage"
            :disable="template?.customParams?.length ? !isFormValid : false"
          />
        </div>
      </q-form>
    </div>
  </div>
</template>

<script setup>
import CloseIcon from "assets/icons/close.svg";
import ChevronDownIcon from "assets/icons/arrow-down.svg";
import { humanizeIdentifier } from "src/assets/scripts/utilities/helpers";

const { toggleTemplateFormDrawer, toggleTemplatesDrawer } = useChatStore();

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
  },
  sendMessage: { type: Function, default: () => {} },
  sendingMessage: { type: Boolean, default: false },
});

const { isFloating } = inject("floating-chat-dialog", {});

// Computed classes based on isFloating
const contentContainerClass = computed(() =>
  unref(isFloating) ? "q-px-md q-py-md" : "q-px-2x-md q-py-lg",
);

const headerTextClass = computed(() =>
  unref(isFloating) ? "text-xs" : "text-sm",
);

const labelClass = computed(() =>
  unref(isFloating) ? "text-info-50 text-xs" : "text-info-50 text-sm",
);

// Computed grid style based on isFloating
const gridStyle = computed(() => ({
  gridTemplateColumns: "auto 1fr",
  gap: unref(isFloating) ? "4px 8px" : "4px 16px",
}));

const formData = ref({});
const previewExpanded = ref(false); // Collapsed by default for floating

watch(
  () => props.template?.customParams,
  (params) => {
    formData.value = {};

    if (params) {
      for (const p of params) {
        formData.value[p.paramName] = "";
      }
    }
  },
  { immediate: true },
);

function formatParamLabel(param) {
  if (!param) return "";

  return param.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function getPrepend(paramValue) {
  if (!paramValue) return null;

  if (paramValue.includes("₹")) return "₹";

  if (paramValue.startsWith("+91")) return "+91";

  return null;
}

const isFormValid = computed(() => {
  return Object.values(formData.value).every((val) => val && val.trim() !== "");
});

const onClose = () => {
  formData.value = {};

  toggleTemplateFormDrawer({ open: false, props: {}, level: 0 });
};

async function onSubmit() {
  if (!isFormValid.value) return;

  const { template } = props;
  const {
    bodyOriginal = "",
    elementName = "",
    header,
    footer,
    buttons = [],
    customParams = [],
  } = template || {};

  // Replace template variables with form data
  let previewBody = bodyOriginal;

  for (const [key, value] of Object.entries(formData.value)) {
    const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");

    previewBody = previewBody?.replace(regex, value || "");
  }

  // Build preview text components
  const headerText = header?.text ? `${header.text}\n` : "";
  const footerText = footer ? `\n${footer}` : "";
  const buttonsText = buttons
    ?.map((b) => b.parameter?.text)
    ?.filter(Boolean)
    ?.join(" ");

  const previewText = [headerText, previewBody, buttonsText, footerText]
    .filter(Boolean)
    .join("\n\n");

  const templateMessage = {
    wa_template: true,
    template_name: elementName,
    broadcast_name: elementName,
    parameters: customParams.map(({ paramName, paramValue }) => ({
      name: paramName,
      value: formData.value[paramName] ?? paramValue ?? "",
    })),
    rendered_body: previewText,
  };

  try {
    await props.sendMessage({ template: templateMessage });

    // Only close for normal drawer mode
    if (!unref(isFloating)) {
      onClose();

      toggleTemplatesDrawer({ open: false });
    }
    // For floating mode, the parent FloatingChatDrawer's sendMessageHandler
    // will handle resetting the view back to chat
  } catch (err) {
    console.error("Error sending template message:", err);
  }
}
</script>
